// src/components/main/produzione/assegnazioniGantt/GanttOrdineLavoroItem.tsx

import React, {useRef, useEffect} from "react";
import {useDrag,} from "react-dnd";
import {DateTime} from "luxon";


import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import {useTheme} from "@mui/material/styles";
import {OrdineLavoro, STATI_ORDINE_LAVORO} from "../../../../ordiniLavoroTypes";
import {GanttOLDragItem, getGanttOLType} from "../GanttBar";
import {getEmptyImage} from "react-dnd-html5-backend";
import AllegatiPreview from "../../../../../../../allegati/AllegatiPreview";
import Tooltip from "@mui/material/Tooltip";
import {Routes_path} from "../../../../../../../routerApp";
import {Link} from "react-router-dom";
import Chip from "@mui/material/Chip";
import {getStatoOdLColor} from "../../../../../../evopCommon/getStatoColor";

// ============================================
// INTERFACES
// ============================================

interface GanttOrdineLavoroItemProps {
    ordineLavoro: OrdineLavoro;
    isSelected: boolean;
    onClick: (ordineLavoroId: number) => void;
}

// ============================================
// COMPONENT
// ============================================

const GanttOrdineLavoroItem = ({
                                   ordineLavoro,
                                   isSelected,
                                   onClick,
                               }: GanttOrdineLavoroItemProps) => {
    const theme = useTheme();
    const ref = useRef<HTMLDivElement>(null);

    const isDone = ordineLavoro.stato === "DONE";

    // Calcola percentuale saturazione
    const valoreConfermato = ordineLavoro.valore_confermato || 0;
    const valoreSchedulato = ordineLavoro.valore_schedulato || 0;
    const percentualeSchedulato =
        valoreConfermato > 0 ? Math.min((valoreSchedulato / valoreConfermato) * 100, 100) : 0;

    // Data da mostrare
    const dataDisplay = ordineLavoro.data_ricevimento
        ? DateTime.fromISO(ordineLavoro.data_ricevimento as string).toFormat("dd/MM")
        : ordineLavoro.data_arrivo_prevista
            ? DateTime.fromISO(ordineLavoro.data_arrivo_prevista as string).toFormat("dd/MM/yy")
            : "-";

    // Drag type basato sulla tecnica
    const dragType = getGanttOLType(ordineLavoro.tecnica);

    const dragItem: GanttOLDragItem = {
        type: dragType,
        ordineLavoroId: ordineLavoro.id!,
        tecnicaId: ordineLavoro.tecnica,
        valoreConfermato: ordineLavoro.valore_confermato,
        // Aggiungo dati per la preview
        codiceItem: ordineLavoro.codice_item,
        tecnicaDisplay: ordineLavoro.tecnica_display,
    };

    const [{isDragging}, drag, preview] = useDrag(
        () => ({
            type: dragType,
            item: dragItem,
            canDrag: !isDone,
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [dragType, dragItem, isDone]
    );

    // Nascondi la preview di default del browser
    useEffect(() => {
        preview(getEmptyImage(), {captureDraggingState: true});
    }, [preview]);

    drag(ref);

    const handleClick = () => {
        if (ordineLavoro.id) {
            onClick(ordineLavoro.id);
        }
    };

    return (
        <Card
            ref={ref}
            onClick={handleClick}
            sx={{
                mb: 0.5,
                cursor: isDone ? "not-allowed" : "grab",
                opacity: isDone ? 0.6 : isDragging ? 0.3 : 1,
                border: isSelected ? `2px solid ${theme.palette.primary.main}` : "1px solid transparent",
                backgroundColor: isSelected
                    ? theme.palette.primary.light + "20"
                    : theme.palette.background.paper,
                transition: "opacity 0.15s, border-color 0.15s",
                "&:hover": {
                    backgroundColor: isDone
                        ? theme.palette.background.paper
                        : theme.palette.action.hover,
                },
            }}
        >
            <CardContent sx={{p: 1, "&:last-child": {pb: 1}}}>
                {/* Header: codice + data */}
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Tooltip sx={{background: 'transparent'}}
                             title={
                                 <AllegatiPreview previsione_id={ordineLavoro.previsione}/>
                             }>
                        <Typography
                            variant="caption"
                            fontWeight="bold"
                            component={Link}
                            target='_blank'
                            to={Routes_path.ORDINE_LAVORO+ordineLavoro.id}
                            sx={{
                                color: isDone ? "text.disabled" : "text.primary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                            }}
                        >
                            {ordineLavoro.codice_item || `#${ordineLavoro.id}`}
                        </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary" >
                        {ordineLavoro.mandatario_display}
                    </Typography>
                    <Tooltip title={STATI_ORDINE_LAVORO[ordineLavoro.stato as keyof typeof STATI_ORDINE_LAVORO] || ordineLavoro.stato}>
                        <Chip color={getStatoOdLColor(ordineLavoro.stato || '')} label={dataDisplay} variant="outlined"/>
                    </Tooltip>
                </Stack>

                {/* Tecnica */}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {ordineLavoro.tecnica_display || "-"}
                </Typography>

                {/* Stagione + Valori */}
                <Stack direction="row" sx={{justifyContent: "space-between", alignItems: "center", mt: 0.5}}>
                    <Typography variant="caption" color="text.secondary" sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "40%",
                    }}>
                        {ordineLavoro.stagione_display || ""}
                    </Typography>
                    <Stack direction="row">
                        <Typography variant="caption" color="text.secondary">
                           val. sched.
                        </Typography>
                        <Typography variant="caption" color="text.primary">
                            € {valoreSchedulato.toLocaleString("it-IT")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            / € {valoreConfermato.toLocaleString("it-IT")}
                        </Typography>
                    </Stack>
                </Stack>

                {/* Progress bar */}
                <LinearProgress
                    variant="determinate"
                    value={percentualeSchedulato}
                    sx={{
                        mt: 0.5,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: theme.palette.grey[300],
                        "& .MuiLinearProgress-bar": {
                            backgroundColor:
                                percentualeSchedulato >= 100
                                    ? theme.palette.success.main
                                    : theme.palette.info.main,
                        },
                    }}
                />
            </CardContent>
        </Card>
    );
};

export default GanttOrdineLavoroItem;